import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config.js';

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightText(text, query) {
  const source = text == null ? '' : String(text);
  if (!query) return source;
  try {
    const safe = escapeRegExp(query);
    const regex = new RegExp(`(${safe})`, 'ig');
    const normalizedQuery = query.toLowerCase();
    const parts = source.split(regex);
    return parts.map((part, index) =>
      part.toLowerCase() === normalizedQuery ? (
        <mark key={index} className="bg-yellow-200 px-0.5">{part}</mark>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    );
  } catch (e) {
    return source;
  }
}

function Home({ currentUser, fetchWithAuth }) {
  const [cockpits, setCockpits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const isAdmin = currentUser.isadmin || currentUser.issuperadmin;

  useEffect(() => {
    const fetchCockpits = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/cockpits/me`);
        const data = await response.json();
        setCockpits(data);

        // Redirection automatique si l'utilisateur n'a qu'un seul cockpit
        if (data.length === 1) {
          navigate(`/c/${data[0].slug}`);
          return;
        }
      } catch (error) {
        console.error('Error fetching cockpits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCockpits();
  }, [currentUser.id, fetchWithAuth, isAdmin, navigate]);

  const filteredCockpits = useMemo(() => {
    if (!searchQuery) return cockpits;
    const q = searchQuery.toLowerCase();
    return cockpits.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const description = (c.description || '').toLowerCase();
      return name.includes(q) || description.includes(q);
    });
  }, [cockpits, searchQuery]);

  useEffect(() => {
    if (!loading && cockpits.length !== 1) {
      if (inputRef.current) inputRef.current.focus();
    }
  }, [loading, cockpits.length]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredCockpits.length === 1) {
      navigate(`/c/${filteredCockpits[0].slug}`);
    }
  };

  // Si on est en cours de redirection, pas besoin d'afficher le loading
  if (cockpits.length === 1) {
    return null;
  }

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {/* <h1 className="text-3xl font-bold mb-4 dark:text-white">Bienvenue sur l'application Globe Cockpit</h1>
      <p className="dark:text-gray-300 mb-8">Cette application regroupe tous les cockpits et monitoring Globe vous concernant.</p> */}

      <div className="max-w-2xl mx-auto mb-8">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Rechercher un cockpit…"
            className="w-full pl-12 pr-4 py-4 text-lg rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {filteredCockpits.length === 1 && searchQuery.trim() !== '' && (
          <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">Appuyez sur Entrée pour ouvrir le cockpit</p>
        )}
      </div>
      {/* <h2 className="text-2xl font-bold mb-6 dark:text-white">Vos cockpits</h2> */}

      {cockpits.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Aucun cockpit disponible :(</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Vous n'avez pas encore accès à des cockpits. Contactez votre administrateur pour obtenir les accès nécessaires.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCockpits.map((cockpit) => (
            <Link
              key={cockpit.id}
              to={`/c/${cockpit.slug}`}
              className="block bg-white dark:bg-gray-800 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <svg className="h-6 w-6 text-blue-500 dark:text-blue-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{highlightText(cockpit.name, searchQuery)}</h3>
                </div>
                {cockpit.description && isAdmin && (
                  <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-2">{highlightText(cockpit.description, searchQuery)}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default Home;